import { FeatureFlags } from '@dupe/common/constants'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import RestyleDropDownMenu from '~/src/components/RestyleDropDownMenu'
import PaywallDrawer from '~/src/components/paywall/PaywallDrawer'
import { useSearchContext } from '~/src/context/SearchProvider'
import useInputImageUpload from '~/src/hooks/useInputImageUpload'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  onFileSelect?: (file: File) => void
  hideRoomRestyle?: boolean
}

const InputImageUpload: React.FC<Props> = ({
  children,
  onFileSelect,
  hideRoomRestyle,
}) => {
  const router = useRouter()
  const { getInputProps } = useSearchContext()

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const filePickerRef = useRef<HTMLInputElement | null>(null)

  const { featureOn: paywallDrawerFeatureEnabled } = useIsFeatureOn(
    FeatureFlags.PaywallDrawer
  )

  const {
    showPaywall,
    setShowPaywall,
    onCameraPress,
    onUploadImage,
    openImagePicker,
  } = useInputImageUpload(filePickerRef)

  // if in iframe and ?camera=true, open camera
  useEffect(() => {
    if (router.query.camera === '1' && filePickerRef.current) {
      filePickerRef.current.click()

      // Remove the camera query param using Next.js router
      const newQuery = { ...router.query }
      delete newQuery.camera

      router.replace(
        {
          pathname:
            router.pathname === '/[hash]'
              ? `/results/${router.pathname}`
              : router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      )
    }
  }, [router, filePickerRef])

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && onFileSelect) {
      onFileSelect(file)
      // Reset the input value so the same file can be selected again
      e.target.value = ''
    }
  }

  // Get input props from context
  const inputProps = getInputProps()

  return (
    <>
      <input
        {...(onFileSelect
          ? {
              ...inputProps,
              onChange: handleFileChange,
              onClick: (e) => e.stopPropagation(), // Prevent parent click handlers
            }
          : inputProps)}
        // accept image files only
        accept='image/*'
        ref={filePickerRef}
      />

      <RestyleDropDownMenu
        dropdownOpen={dropdownOpen}
        setDropdownOpen={setDropdownOpen}
        onUploadImage={onUploadImage}
        onCameraPress={onCameraPress}
        openImagePicker={openImagePicker}
        hideRoomRestyle={hideRoomRestyle}
      >
        {children}
      </RestyleDropDownMenu>

      {paywallDrawerFeatureEnabled && (
        <PaywallDrawer
          isOpen={showPaywall}
          onOpenChange={setShowPaywall}
          source='camera-feature'
          featureName='Camera Search'
          onSuccess={() => {
            if (filePickerRef.current) {
              filePickerRef.current.click()
            }
          }}
        />
      )}
    </>
  )
}

export default InputImageUpload
